import React, {useEffect, useRef, useState} from 'react';
import ModalDefault from '../../../common/components/modal/modal-default';
import ContactInfoDialog from '../../../common/components/modal/contact-info-dialog';
import EyeIcon from '@heroicons/react/20/solid/EyeIcon';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';

Chart.register(annotationPlugin);

const formatTrendData = (periods) => {
    if (!periods?.length) return [];

    const byIndex = {};
    let maxIndex = 0;
    periods.forEach(p => {
        byIndex[p.PeriodIndex] = p;
        if (p.PeriodIndex > maxIndex) maxIndex = p.PeriodIndex;
    });

    const refPeriod = byIndex[0] ?? Object.values(byIndex)[0];
    const periodMs = new Date(refPeriod.PeriodEnd) - new Date(refPeriod.PeriodStart) + 86400000;
    const refIndex = parseInt(refPeriod.PeriodIndex);
    const refStart = new Date(refPeriod.PeriodStart).getTime();
    const refEnd = new Date(refPeriod.PeriodEnd).getTime();

    const fmt = (ts) => new Date(ts).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
    const shortFmt = (ts) => new Date(ts).toLocaleDateString('en-US', {month: 'short', year: '2-digit'});

    const results = [];
    for (let i = maxIndex; i >= 0; i--) {
        const p = byIndex[i];
        if (p) {
            const pStart = new Date(p.PeriodStart).getTime();
            const pEnd = new Date(p.PeriodEnd).getTime();
            const total = Object.values(p.Accounts).reduce((s, v) => s + v, 0);
            results.push({
                label: `${fmt(pStart)} – ${fmt(pEnd)}`,
                shortLabel: shortFmt(pStart),
                total,
                accounts: p.Accounts,
                contacts: p.Contacts ?? [],
                isCurrent: i === 0,
                periodIndex: i,
                _raw: p,
            });
        } else {
            const delta = (i - refIndex) * periodMs;
            const mStart = refStart - delta;
            const mEnd = refEnd - delta;
            results.push({
                label: `${fmt(mStart)} – ${fmt(mEnd)}`,
                shortLabel: shortFmt(mStart),
                total: 0,
                accounts: {},
                contacts: [],
                isCurrent: i === 0,
                periodIndex: i,
                _raw: null,
            });
        }
    }
    return results;
};

const TrendChart = ({chartData, color}) => {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current || !chartData.length) return;
        if (chartRef.current) chartRef.current.destroy();

        const ctx = canvasRef.current.getContext('2d');
        const avg = chartData.reduce((s, d) => s + d.total, 0) / chartData.length;

        chartRef.current = new Chart(ctx, {
            type: 'line',
            data: {
                labels: chartData.map(d => d.shortLabel),
                datasets: [{
                    data: chartData.map(d => d.total),
                    borderColor: color,
                    backgroundColor: color + '14',
                    borderWidth: 2.5,
                    pointRadius: chartData.map(d => d.isCurrent ? 6 : 4),
                    pointBackgroundColor: chartData.map(d => d.isCurrent ? color : 'transparent'),
                    pointBorderColor: color,
                    pointBorderWidth: 2,
                    pointHoverRadius: 7,
                    tension: 0.3,
                    fill: true,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {intersect: false, mode: 'index'},
                plugins: {
                    legend: {display: false},
                    tooltip: {
                        backgroundColor: '#fff',
                        titleColor: '#111827',
                        bodyColor: '#4b5563',
                        borderColor: '#d1d5db',
                        borderWidth: 1,
                        padding: 12,
                        cornerRadius: 8,
                        titleFont: {weight: '600', size: 13},
                        bodyFont: {size: 12},
                        displayColors: false,
                        callbacks: {
                            title: (items) => chartData[items[0].dataIndex]?.label || '',
                            label: (item) => {
                                const d = chartData[item.dataIndex];
                                return `Net: $${d.total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
                            },
                        },
                    },
                    annotation: {
                        annotations: {
                            avgLine: {
                                type: 'line',
                                yMin: avg,
                                yMax: avg,
                                borderColor: '#9ca3af',
                                borderWidth: 1,
                                borderDash: [4, 4],
                                label: {
                                    display: true,
                                    content: `Avg $${avg.toFixed(2)}`,
                                    position: 'end',
                                    backgroundColor: 'transparent',
                                    color: '#9ca3af',
                                    font: {size: 11},
                                },
                            },
                            zeroLine: {
                                type: 'line',
                                yMin: 0,
                                yMax: 0,
                                borderColor: '#ef444466',
                                borderWidth: 1,
                                borderDash: [4, 4],
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        grid: {display: false},
                        ticks: {color: '#6b7280', font: {size: 11}},
                        border: {color: '#e5e7eb'},
                    },
                    y: {
                        grid: {color: '#f3f4f6'},
                        ticks: {
                            color: '#6b7280',
                            font: {size: 11},
                            callback: (v) => `$${v.toLocaleString()}`,
                        },
                        border: {display: false},
                    },
                },
            },
        });

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [chartData]);

    return <canvas ref={canvasRef}/>;
};

const fmt$ = (v) => `$${Number(v).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
const fmtDiff = (v) => Number(v).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});

const AdvancesTrend = ({data, isLoading, translate}) => {
    const [selectedContact, setSelectedContact] = useState(null)
    const [contactInfoContact, setContactInfoContact] = useState(null)

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"/>
            </div>
        );
    }

    if (!data) return null;

    const {periods = [], accounts = {}, top10Debt = [], top10Accumulators = [], top10PaidOff = []} = data;
    const chartData = formatTrendData(periods);

    const getContactAccountSummary = (contactID) => {
        const currentPeriod = periods.find(p => p.PeriodIndex === 0)
        if (!currentPeriod) return []
        const contact = (currentPeriod.Contacts ?? []).find(c => c.ContactID === contactID)
        if (!contact) return []
        return Object.entries(contact.Accounts ?? {})
            .filter(([, v]) => v !== 0)
            .map(([accID, val]) => ({accID, label: accounts[accID] ?? accID, val}))
            .sort((a, b) => b.val - a.val)
    }

    const activeAccountIDs = Object.keys(accounts).filter(id =>
        periods.some(p => p.Accounts?.[id] !== undefined && p.Accounts[id] !== 0)
    );

    const styles = getComputedStyle(document.documentElement);
    const primaryColor = styles.getPropertyValue('--color-primary').trim() || '#4f46e5';

    const renderTop10Table = ({title, rows, valueKey, valueLabel, colorClass}) => (
        <div>
            <p className="text-xs font-semibold text-tm-gray-500 uppercase tracking-wide mb-2">{title}</p>
            <div className="border border-tm-gray-300 rounded-card overflow-hidden">
                <table className="min-w-full text-sm divide-y divide-tm-gray-300">
                    <thead className="bg-tm-gray-50">
                    <tr>
                        <th className="px-3 py-2 text-left font-medium text-tm-gray-700 w-6">#</th>
                        <th className="px-3 py-2 text-left font-medium text-tm-gray-700">Name</th>
                        <th className="px-3 py-2 text-right font-medium text-tm-gray-700">{valueLabel}</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-tm-gray-200">
                    {rows.length ? rows.map((it, i) => (
                        <tr key={it.ContactID} className="bg-popup">
                            <td className="px-3 py-2 text-tm-gray-400 text-xs">{i + 1}</td>
                            <td className="px-3 py-2 flex items-center gap-2">
                                <button
                                    className="text-primary hover:underline text-left"
                                    onClick={() => setContactInfoContact(it)}
                                >
                                    {it.FirstName} {it.LastName}
                                </button>
                                <button
                                    className="text-tm-gray-400 hover:text-tm-gray-700 flex-shrink-0"
                                    onClick={() => setSelectedContact(it)}
                                    title="View account breakdown"
                                >
                                    <EyeIcon className="w-4 h-4"/>
                                </button>
                            </td>
                            <td className={`px-3 py-2 text-right font-semibold tabular-nums ${colorClass}`}>
                                {fmt$(it[valueKey])}
                            </td>
                        </tr>
                    )) : (
                        <tr><td colSpan={3} className="px-3 py-6 text-center text-tm-gray-400 text-sm">No data</td></tr>
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="py-4 px-6 grid grid-cols-5 gap-6 items-start">
            {/* Left 3/5: Chart + Period Breakdown */}
            <div className="col-span-3 space-y-5">
                <div>
                    <p className="text-xs font-semibold text-tm-gray-500 uppercase tracking-wide mb-2">Net Balance Movement by Period</p>
                    <div className="bg-popup border border-tm-gray-300 rounded-card p-4" style={{height: 300}}>
                        {chartData.length ? (
                            <TrendChart chartData={chartData} color={primaryColor}/>
                        ) : (
                            <div className="flex items-center justify-center h-full text-sm text-tm-gray-400">No data</div>
                        )}
                    </div>
                </div>

                <div>
                    <p className="text-xs font-semibold text-tm-gray-500 uppercase tracking-wide mb-2">Period Breakdown</p>
                    <div className="border border-tm-gray-300 rounded-card overflow-auto">
                        <table className="text-sm divide-y divide-tm-gray-300">
                            <thead className="bg-tm-gray-50">
                            <tr>
                                <th className="px-4 py-2.5 text-left font-medium text-tm-gray-700 whitespace-nowrap sticky left-0 bg-tm-gray-50 z-10">Account</th>
                                {chartData.map((col) => (
                                    <th key={col.periodIndex} className={`px-4 py-2.5 text-right font-medium text-tm-gray-700 whitespace-nowrap min-w-[9rem] ${col.isCurrent ? 'bg-primary/5' : ''}`}>
                                        <div>{col.label}</div>
                                        {col.isCurrent && (
                                            <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">Current</span>
                                        )}
                                    </th>
                                ))}
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-tm-gray-200">
                            <tr className="bg-popup font-semibold">
                                <td className="px-4 py-2.5 text-tm-gray-900 whitespace-nowrap sticky left-0 bg-popup">Total</td>
                                {chartData.map((col, ci) => {
                                    const prev = ci > 0 ? chartData[ci - 1].total : null;
                                    const delta = prev !== null ? col.total - prev : null;
                                    const color = delta === null ? 'text-tm-gray-700' : delta > 0 ? 'text-red-500' : delta < 0 ? 'text-green-600' : 'text-tm-gray-400';
                                    return (
                                        <td key={col.periodIndex} className={`px-4 py-2.5 text-right tabular-nums ${color} ${col.isCurrent ? 'bg-primary/5' : ''}`}>
                                            {fmt$(col.total)}
                                            {delta !== null && delta !== 0 && (
                                                <div className="text-xs font-normal opacity-70">({delta > 0 ? '+' : ''}{fmtDiff(delta)})</div>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                            {activeAccountIDs.map(id => (
                                <tr key={id} className="bg-popup">
                                    <td className="px-4 py-2.5 text-tm-gray-600 whitespace-nowrap sticky left-0 bg-popup text-xs">{accounts[id]}</td>
                                    {chartData.map((col, ci) => {
                                        const val = col.accounts?.[id] ?? 0;
                                        const prevVal = ci > 0 ? (chartData[ci - 1].accounts?.[id] ?? 0) : null;
                                        const delta = prevVal !== null ? val - prevVal : null;
                                        const color = delta === null ? 'text-tm-gray-700' : delta > 0 ? 'text-red-500' : delta < 0 ? 'text-green-600' : 'text-tm-gray-400';
                                        return (
                                            <td key={col.periodIndex} className={`px-4 py-2.5 text-right tabular-nums ${color} ${col.isCurrent ? 'bg-primary/5' : ''}`}>
                                                {val !== 0 ? fmt$(val) : '—'}
                                                {delta !== null && delta !== 0 && (
                                                    <div className="text-xs font-normal opacity-70">({delta > 0 ? '+' : ''}{fmtDiff(delta)})</div>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Right 2/5: Top 10 tables */}
            <div className="col-span-2 space-y-4 pb-6">
                {renderTop10Table({title: 'Top 10 Worst Debt', rows: top10Debt, valueKey: 'Balance', valueLabel: 'Balance', colorClass: 'text-red-500'})}
                {renderTop10Table({title: 'Top 10 Accumulators', rows: top10Accumulators, valueKey: 'Delta', valueLabel: 'Added', colorClass: 'text-red-500'})}
                {renderTop10Table({title: 'Top 10 Payers', rows: top10PaidOff, valueKey: 'Delta', valueLabel: 'Paid Off', colorClass: 'text-green-600'})}
            </div>

            {contactInfoContact && (
                <ContactInfoDialog
                    show={true}
                    contactID={contactInfoContact.ContactID}
                    translate={translate}
                    handleClose={() => setContactInfoContact(null)}
                />
            )}

            {selectedContact && (() => {
                const summary = getContactAccountSummary(selectedContact.ContactID)
                return (
                    <ModalDefault
                        show={true}
                        title={`${selectedContact.FirstName} ${selectedContact.LastName} - Advances Account Breakdown`}
                        widthClass="max-w-md"
                        onClose={() => setSelectedContact(null)}
                        closeButtonLabel="Close"
                    >
                        <div className="p-4">
                            {summary.length === 0 ? (
                                <p className="text-sm text-tm-gray-400 text-center py-4">No account data found</p>
                            ) : (
                                <table className="min-w-full text-sm divide-y divide-tm-gray-200">
                                    <thead className="bg-tm-gray-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-medium text-tm-gray-700">Account</th>
                                        <th className="px-3 py-2 text-right font-medium text-tm-gray-700">Balance</th>
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-tm-gray-200">
                                    {summary.map(({accID, label, val}) => (
                                        <tr key={accID} className="bg-popup">
                                            <td className="px-3 py-2 text-tm-gray-700">{label}</td>
                                            <td className={`px-3 py-2 text-right font-semibold tabular-nums ${val > 0 ? 'text-red-500' : 'text-green-600'}`}>
                                                {fmt$(val)}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </ModalDefault>
                )
            })()}
        </div>
    );
};

export default AdvancesTrend;
